import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { MdModeEditOutline, MdDeleteOutline } from 'react-icons/md';
import './AdminPanel.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function AdminPanel() {
  const [searchParams] = useSearchParams();
  const merchantId = searchParams.get('merchant_id');
  
  // Состояние для аутентификации
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false); // Флаг проверки аутентификации
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  
  const [merchant, setMerchant] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('products'); // 'products' или 'orders'
  
  // Форма добавления товара
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    image_url: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Состояние для переименования каталога
  const [isRenamingCatalog, setIsRenamingCatalog] = useState(false);
  const [catalogNameInput, setCatalogNameInput] = useState('');
  
  // Состояние для фильтрации заказов
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Состояние для вида отображения заказов
  const [ordersViewMode, setOrdersViewMode] = useState('cards'); // 'cards' или 'table'

  // Проверка сохраненной аутентификации при загрузке
  useEffect(() => {
    // Проверяем сохраненную аутентификацию только если она есть
    const savedAuth = localStorage.getItem('admin_authenticated') || 
                      sessionStorage.getItem('admin_authenticated');
    console.log('🔐 Проверка сохраненной аутентификации:', savedAuth);
    if (savedAuth === 'true') {
      console.log('✅ Найдена сохраненная аутентификация');
      setIsAuthenticated(true);
    } else {
      console.log('❌ Сохраненная аутентификация не найдена, показываем форму входа');
      setIsAuthenticated(false);
    }
    setAuthChecked(true); // Помечаем, что проверка завершена
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';
    
    if (password === adminPassword) {
      setIsAuthenticated(true);
      
      if (rememberMe) {
        // Сохраняем в localStorage (сохраняется после закрытия браузера)
        localStorage.setItem('admin_authenticated', 'true');
      } else {
        // Сохраняем в sessionStorage (очищается при закрытии браузера)
        sessionStorage.setItem('admin_authenticated', 'true');
      }
      
      setAuthError('');
      setPassword('');
    } else {
      setAuthError('Неверный пароль');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('admin_authenticated');
    sessionStorage.removeItem('admin_authenticated');
  };

  useEffect(() => {
    // Загружаем данные только если авторизованы
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    // Сбрасываем состояние загрузки при начале новой загрузки
    setLoading(true);
    setError(null);
    
    const defaultCatalogId = process.env.REACT_APP_DEFAULT_CATALOG_ID;
    
    // Если merchant_id указан, используем его (для обратной совместимости)
    // Иначе используем дефолтный каталог
    if (merchantId) {
      // Загружаем информацию об администраторе и каталоге по merchant_id
      axios.get(`${API_URL}/merchants/${merchantId}`)
        .then(response => {
          setMerchant(response.data);
          return axios.get(`${API_URL}/merchants/${merchantId}/catalog`);
        })
        .then(response => {
          const catalogData = response.data;
          setCatalog(catalogData);
          return Promise.all([
            axios.get(`${API_URL}/catalog/${catalogData.id}/products`),
            axios.get(`${API_URL}/orders/catalog/${catalogData.id}`)
          ]);
        })
        .then(([productsResponse, ordersResponse]) => {
          setProducts(productsResponse.data);
          setOrders(ordersResponse.data || []);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error loading admin data:', err);
          setError('Ошибка загрузки данных. Проверьте правильность merchant_id.');
          setLoading(false);
        });
    } else if (defaultCatalogId) {
      // Загружаем каталог напрямую по ID
      console.log('📡 Загрузка каталога:', defaultCatalogId);
      axios.get(`${API_URL}/catalog/${defaultCatalogId}`)
        .then(response => {
          console.log('✅ Каталог загружен:', response.data);
          const catalogData = response.data;
          setCatalog(catalogData);
          return Promise.all([
            axios.get(`${API_URL}/catalog/${catalogData.id}/products`),
            axios.get(`${API_URL}/orders/catalog/${catalogData.id}`)
          ]);
        })
        .then(([productsResponse, ordersResponse]) => {
          console.log('✅ Товары загружены:', productsResponse.data?.length || 0);
          console.log('✅ Заказы загружены:', ordersResponse.data?.length || 0);
          setProducts(productsResponse.data || []);
          setOrders(ordersResponse.data || []);
          setLoading(false);
        })
        .catch(err => {
          console.error('❌ Ошибка загрузки данных:', err);
          console.error('❌ Детали ошибки:', err.response?.data || err.message);
          setError(`Ошибка загрузки данных: ${err.response?.data?.error || err.message}. Проверьте правильность DEFAULT_CATALOG_ID: ${defaultCatalogId}`);
          setLoading(false);
        });
    } else {
      setError('Не указан идентификатор администратора или каталога. Добавьте ?merchant_id=YOUR_MERCHANT_ID к URL или настройте REACT_APP_DEFAULT_CATALOG_ID');
      setLoading(false);
    }
  }, [merchantId, isAuthenticated]);

  const loadProducts = () => {
    if (catalog) {
      axios.get(`${API_URL}/catalog/${catalog.id}/products`)
        .then(response => setProducts(response.data))
        .catch(err => console.error('Error loading products:', err));
    }
  };

  const loadOrders = (useCurrentFilters = true) => {
    if (catalog) {
      const params = new URLSearchParams();
      // Используем текущие значения фильтров, если не указано иное
      const fromDate = useCurrentFilters ? dateFrom : '';
      const toDate = useCurrentFilters ? dateTo : '';
      
      if (fromDate) params.append('date_from', fromDate);
      if (toDate) params.append('date_to', toDate);
      
      const url = `${API_URL}/orders/catalog/${catalog.id}${params.toString() ? '?' + params.toString() : ''}`;
      console.log('📡 Loading orders from:', url);
      console.log('📅 Filters:', { fromDate, toDate });
      axios.get(url)
        .then(response => {
          console.log('✅ Orders loaded:', response.data?.length || 0, 'orders');
          setOrders(response.data || []);
        })
        .catch(err => {
          console.error('❌ Error loading orders:', err);
          alert('Ошибка при загрузке заказов: ' + (err.response?.data?.error || err.message));
        });
    }
  };

  const handleRenameCatalog = async () => {
    if (!catalogNameInput.trim()) {
      alert('Введите название каталога');
      return;
    }

    try {
      console.log('Renaming catalog:', catalog.id, 'to:', catalogNameInput.trim());
      const response = await axios.put(`${API_URL}/catalog/${catalog.id}`, {
        name: catalogNameInput.trim()
      });
      console.log('Catalog renamed successfully:', response.data);
      // Обновляем каталог в состоянии
      setCatalog({ ...catalog, name: catalogNameInput.trim() });
      setIsRenamingCatalog(false);
      setCatalogNameInput('');
    } catch (err) {
      console.error('Error renaming catalog:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Ошибка при переименовании каталога';
      alert(`Ошибка при переименовании каталога: ${errorMessage}`);
    }
  };

  const handleFilterOrders = () => {
    console.log('🔍 Applying date filter:', { dateFrom, dateTo });
    loadOrders();
  };

  const handleClearDateFilter = () => {
    console.log('🧹 Clearing date filter');
    setDateFrom('');
    setDateTo('');
    // Перезагружаем заказы без фильтров
    // Используем setTimeout чтобы состояние успело обновиться
    setTimeout(() => {
      loadOrders(false); // false = не использовать фильтры
    }, 100);
  };

  const handleExportToExcel = () => {
    if (orders.length === 0) {
      alert('Нет заказов для экспорта');
      return;
    }

    // Подготавливаем данные для Excel
    const excelData = [];

    orders.forEach(order => {
      // Парсим JSON строку с товарами заказа
      let orderItems = [];
      try {
        if (order.items) {
          if (typeof order.items === 'string') {
            const parsed = JSON.parse(order.items);
            orderItems = Array.isArray(parsed) ? parsed : [];
          } else if (Array.isArray(order.items)) {
            orderItems = order.items;
          }
        }
      } catch (e) {
        console.error('Error parsing order items for export:', e);
      }

      if (orderItems.length === 0) {
        // Если нет товаров, добавляем одну строку
        excelData.push({
          'Дата': new Date(order.created_at).toLocaleString('ru-RU'),
          'ID заказа': order.id.substring(0, 8),
          'Клиент': order.customer_name,
          'Телефон': order.customer_phone,
          'Адрес доставки': order.address || 'не указан',
          'Комментарий': order.comment || '—',
          'Позиция': 'Нет товаров',
          'Количество': '',
          'Сумма позиции': '',
          'Общая сумма': order.total_amount
        });
      } else {
        // Для каждого товара создаем отдельную строку
        orderItems.forEach((item, itemIndex) => {
          excelData.push({
            'Дата': itemIndex === 0 ? new Date(order.created_at).toLocaleString('ru-RU') : '',
            'ID заказа': itemIndex === 0 ? order.id.substring(0, 8) : '',
            'Клиент': itemIndex === 0 ? order.customer_name : '',
            'Телефон': itemIndex === 0 ? order.customer_phone : '',
            'Адрес доставки': itemIndex === 0 ? (order.address || 'не указан') : '',
            'Комментарий': itemIndex === 0 ? (order.comment || '—') : '',
            'Позиция': item.product_name || 'Товар',
            'Количество': item.quantity,
            'Сумма позиции': (item.quantity * item.price).toFixed(2),
            'Общая сумма': itemIndex === 0 ? order.total_amount : ''
          });
        });
      }
    });

    // Создаем рабочую книгу Excel
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Заказы');

    // Генерируем имя файла с датой
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = `заказы_${dateStr}.xlsx`;

    // Сохраняем файл
    XLSX.writeFile(wb, fileName);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Создаем превью
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) {
      return productForm.image_url; // Возвращаем существующий URL, если файл не выбран
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Возвращаем полный URL (используем текущий домен)
      const baseUrl = window.location.origin;
      return `${baseUrl}${response.data.url}`;
    } catch (err) {
      console.error('Error uploading image:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Ошибка при загрузке изображения';
      throw new Error(errorMessage);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Загружаем изображение, если выбран файл
      let imageUrl = productForm.image_url;
      if (imageFile) {
        imageUrl = await uploadImage();
      }
      
      if (editingProduct) {
        // Обновление товара
        await axios.put(`${API_URL}/catalog/products/${editingProduct.id}`, {
          name: productForm.name,
          description: productForm.description,
          price: parseFloat(productForm.price),
          image_url: imageUrl
        });
      } else {
        // Создание нового товара
        await axios.post(`${API_URL}/catalog/${catalog.id}/products`, {
          name: productForm.name,
          description: productForm.description,
          price: parseFloat(productForm.price),
          image_url: imageUrl
        });
      }
      
      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm({ name: '', description: '', price: '', image_url: '' });
      setImageFile(null);
      setImagePreview(null);
      loadProducts();
    } catch (err) {
      console.error('Error saving product:', err);
      alert(err.message || 'Ошибка при сохранении товара');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      image_url: product.image_url || ''
    });
    setImageFile(null);
    setImagePreview(product.image_url || null);
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот товар?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/catalog/products/${productId}`);
      loadProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
      alert('Ошибка при удалении товара');
    }
  };

  // Если не авторизован, показываем форму входа
  if (!authChecked) {
    // Ждем завершения проверки аутентификации
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f5f5f5'
      }}>
        <div style={{ color: '#666' }}>Проверка аутентификации...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f5f5f5'
      }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxWidth: '400px',
          width: '90%'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#333' }}>Вход в админ-панель</h2>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                Пароль:
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
              {authError && (
                <p style={{ color: '#d32f2f', marginTop: '0.5rem', marginBottom: 0, fontSize: '0.875rem' }}>
                  {authError}
                </p>
              )}
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#666' }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ marginRight: '0.5rem', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Запомнить меня</span>
              </label>
            </div>
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#1565c0'}
              onMouseOut={(e) => e.target.style.background = '#1976d2'}
            >
              Войти
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading">Загрузка панели управления...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-panel">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <h1 style={{ margin: 0 }}>Панель управления каталогом</h1>
          <button
            onClick={handleLogout}
            style={{
              padding: '0.5rem 1rem',
              background: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Выйти
          </button>
        </div>
        {catalog && (
          <div className="catalog-info">
            {!isRenamingCatalog ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <p style={{ margin: 0 }}>Каталог: <strong>{catalog.name}</strong></p>
                  <button
                    className="btn-edit"
                    onClick={() => {
                      setCatalogNameInput(catalog.name);
                      setIsRenamingCatalog(true);
                    }}
                    style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    <MdModeEditOutline style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} /> Переименовать
                  </button>
                </div>
                <p style={{ margin: '0.25rem 0 0 0' }}>ID: {catalog.id.substring(0, 8)}...</p>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={catalogNameInput}
                  onChange={(e) => setCatalogNameInput(e.target.value)}
                  placeholder="Название каталога"
                  style={{
                    padding: '0.5rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    flex: '1',
                    minWidth: '200px'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleRenameCatalog();
                    } else if (e.key === 'Escape') {
                      setIsRenamingCatalog(false);
                      setCatalogNameInput('');
                    }
                  }}
                  autoFocus
                />
                <button
                  className="btn-primary"
                  onClick={handleRenameCatalog}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  Сохранить
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setIsRenamingCatalog(false);
                    setCatalogNameInput('');
                  }}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  Отмена
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <div className="admin-tabs">
        <button
          className={activeTab === 'products' ? 'active' : ''}
          onClick={() => {
            setActiveTab('products');
            loadProducts();
          }}
        >
          Товары
        </button>
        <button
          className={activeTab === 'orders' ? 'active' : ''}
          onClick={() => {
            setActiveTab('orders');
            loadOrders();
          }}
        >
          Заказы
        </button>
      </div>

      {activeTab === 'products' && (
        <div className="admin-content">
          <div className="admin-actions">
            <button
              className="btn-primary"
              onClick={() => {
                setEditingProduct(null);
                setProductForm({ name: '', description: '', price: '', image_url: '' });
                setShowProductForm(true);
              }}
            >
              + Добавить товар
            </button>
          </div>

          {showProductForm && (
            <div className="product-form-modal">
              <div className="product-form">
                <h2>{editingProduct ? 'Редактировать товар' : 'Добавить товар'}</h2>
                <form onSubmit={handleProductSubmit}>
                  <div className="form-group">
                    <label>Название *</label>
                    <input
                      type="text"
                      value={productForm.name}
                      onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Описание</label>
                    <textarea
                      value={productForm.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Цена (₽) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={productForm.price}
                      onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Изображение</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleImageChange}
                        style={{ padding: '0.5rem' }}
                      />
                      {imagePreview && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <img 
                            src={imagePreview} 
                            alt="Превью" 
                            style={{ 
                              maxWidth: '200px', 
                              maxHeight: '200px', 
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0'
                            }} 
                          />
                        </div>
                      )}
                      {!imageFile && productForm.image_url && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <p style={{ fontSize: '0.875rem', color: '#666', margin: '0 0 0.5rem 0' }}>
                            Текущее изображение:
                          </p>
                          <img 
                            src={productForm.image_url} 
                            alt="Текущее" 
                            style={{ 
                              maxWidth: '200px', 
                              maxHeight: '200px', 
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0'
                            }} 
                          />
                        </div>
                      )}
                      {uploadingImage && (
                        <p style={{ fontSize: '0.875rem', color: '#1976d2' }}>
                          Загрузка изображения...
                        </p>
                      )}
                    </div>
                    <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>
                      Или введите URL изображения:
                    </p>
                    <input
                      type="url"
                      value={productForm.image_url}
                      onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      disabled={!!imageFile}
                      style={{ marginTop: '0.25rem' }}
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn-primary" disabled={uploadingImage}>
                      {uploadingImage ? 'Загрузка...' : (editingProduct ? 'Сохранить' : 'Добавить')}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setShowProductForm(false);
                        setEditingProduct(null);
                        setProductForm({ name: '', description: '', price: '', image_url: '' });
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                      disabled={uploadingImage}
                    >
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="products-list">
            {loading ? (
              <p className="empty-state">Загрузка товаров...</p>
            ) : error ? (
              <p className="empty-state" style={{ color: '#d32f2f' }}>{error}</p>
            ) : !catalog ? (
              <p className="empty-state">Каталог не загружен. Проверьте консоль браузера.</p>
            ) : products.length === 0 ? (
              <p className="empty-state">Товаров пока нет. Добавьте первый товар!</p>
            ) : (
              products.map(product => (
                <div key={product.id} className="product-item">
                  {product.image_url && (
                    <div className="product-image">
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        onError={(e) => {
                          console.error('Ошибка загрузки изображения:', product.image_url);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="product-details">
                    <h3>{product.name}</h3>
                    {product.description && <p>{product.description}</p>}
                    <p className="product-price">{product.price} ₽</p>
                  </div>
                  <div className="product-actions">
                    <button
                      className="btn-edit"
                      onClick={() => handleEditProduct(product)}
                    >
                      <MdModeEditOutline style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} /> Редактировать
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteProduct(product.id)}
                    >
                      <MdDeleteOutline style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} /> Удалить
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="admin-content">
          <div className="orders-filters" style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '8px', 
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>Фильтр по датам</h3>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                  От:
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                  До:
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn-primary"
                  onClick={handleFilterOrders}
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  Применить
                </button>
                {(dateFrom || dateTo) && (
                  <button
                    className="btn-secondary"
                    onClick={handleClearDateFilter}
                    style={{ padding: '0.75rem 1.5rem' }}
                  >
                    Сбросить
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Переключатель вида отображения и кнопка экспорта */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1rem',
            gap: '0.5rem',
            flexWrap: 'wrap'
          }}>
            <button
              className="btn-primary"
              onClick={handleExportToExcel}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
              disabled={orders.length === 0}
            >
              📥 Выгрузить в Excel
            </button>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className={ordersViewMode === 'cards' ? 'btn-primary' : 'btn-secondary'}
                onClick={() => setOrdersViewMode('cards')}
                style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
              >
                Карточки
              </button>
              <button
                className={ordersViewMode === 'table' ? 'btn-primary' : 'btn-secondary'}
                onClick={() => setOrdersViewMode('table')}
                style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
              >
                Таблица
              </button>
            </div>
          </div>

          {orders.length === 0 ? (
            <p className="empty-state">Заказов пока нет</p>
          ) : ordersViewMode === 'cards' ? (
            <div className="orders-list">
              {orders.map(order => {
                // Парсим JSON строку с товарами заказа
                let orderItems = [];
                try {
                  if (order.items) {
                    if (typeof order.items === 'string') {
                      // Если это строка, пытаемся распарсить
                      const parsed = JSON.parse(order.items);
                      orderItems = Array.isArray(parsed) ? parsed : [];
                    } else if (Array.isArray(order.items)) {
                      orderItems = order.items;
                    }
                  }
                  console.log('Order items for', order.id, ':', orderItems);
                } catch (e) {
                  console.error('Error parsing order items for', order.id, ':', e, 'Raw items:', order.items);
                }

                return (
                  <div key={order.id} className="order-item">
                    <div className="order-header">
                      <h3>Заказ #{order.id.substring(0, 8)}</h3>
                      <span className="order-date">
                        {new Date(order.created_at).toLocaleString('ru-RU')}
                      </span>
                    </div>
                    <div className="order-info">
                      <p><strong>Клиент:</strong> {order.customer_name}</p>
                      <p><strong>Телефон:</strong> {order.customer_phone}</p>
                      <p><strong>Адрес доставки:</strong> {order.address || 'не указан'}</p>
                      {order.comment && <p><strong>Комментарий:</strong> {order.comment}</p>}
                      
                      {/* Состав заказа */}
                      {orderItems && orderItems.length > 0 && (
                        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f0f0f0' }}>
                          <p style={{ marginBottom: '0.5rem', fontWeight: '600' }}><strong>Состав заказа:</strong></p>
                          <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyle: 'disc' }}>
                            {orderItems.map((item, idx) => (
                              <li key={idx} style={{ marginBottom: '0.25rem' }}>
                                {item.product_name || 'Товар'} — {item.quantity} шт. × {item.price} ₽ = {(item.quantity * item.price).toFixed(2)} ₽
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <p style={{ marginTop: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>
                        <strong>Общая сумма:</strong> {order.total_amount} ₽
                      </p>
                      <p><strong>Статус:</strong> {order.status || 'новый'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="orders-table-container" style={{ 
              background: 'white', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              overflowX: 'auto'
            }}>
              <table className="orders-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Дата</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>ID</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Клиент</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Телефон</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', maxWidth: '200px' }}>Адрес</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', maxWidth: '200px' }}>Комментарий</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Позиция</th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', fontSize: '0.875rem' }}>Количество</th>
                    <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', fontSize: '0.875rem' }}>Сумма позиции</th>
                    <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', fontSize: '0.875rem' }}>Общая сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => {
                    // Парсим JSON строку с товарами заказа
                    let orderItems = [];
                    try {
                      if (order.items) {
                        if (typeof order.items === 'string') {
                          const parsed = JSON.parse(order.items);
                          orderItems = Array.isArray(parsed) ? parsed : [];
                        } else if (Array.isArray(order.items)) {
                          orderItems = order.items;
                        }
                      }
                      console.log('Order items for', order.id, ':', orderItems);
                    } catch (e) {
                      console.error('Error parsing order items for', order.id, ':', e, 'Raw items:', order.items);
                    }

                    // Если нет товаров, показываем одну строку с сообщением
                    if (orderItems.length === 0) {
                      return (
                        <tr key={order.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                            {new Date(order.created_at).toLocaleDateString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                            {order.id.substring(0, 8)}
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                            {order.customer_name}
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                            {order.customer_phone}
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', maxWidth: '200px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                            {order.address || 'не указан'}
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', maxWidth: '200px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                            {order.comment || '—'}
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#999' }} colSpan="2">
                            Нет товаров
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', textAlign: 'right' }}>
                            —
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', textAlign: 'right', fontWeight: '600' }}>
                            {order.total_amount} ₽
                          </td>
                        </tr>
                      );
                    }

                    // Для каждого товара создаем отдельную строку
                    return orderItems.map((item, itemIndex) => (
                      <tr 
                        key={`${order.id}-${itemIndex}`} 
                        style={{ 
                          borderBottom: itemIndex === orderItems.length - 1 ? '2px solid #e0e0e0' : '1px solid #f0f0f0',
                          backgroundColor: itemIndex === 0 ? 'transparent' : '#fafafa'
                        }}
                      >
                        {/* Показываем данные заказа только в первой строке */}
                        {itemIndex === 0 && (
                          <>
                            <td 
                              style={{ 
                                padding: '1rem', 
                                fontSize: '0.875rem',
                                verticalAlign: 'top'
                              }}
                              rowSpan={orderItems.length}
                            >
                              {new Date(order.created_at).toLocaleDateString('ru-RU', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td 
                              style={{ 
                                padding: '1rem', 
                                fontSize: '0.875rem', 
                                fontFamily: 'monospace',
                                verticalAlign: 'top'
                              }}
                              rowSpan={orderItems.length}
                            >
                              {order.id.substring(0, 8)}
                            </td>
                            <td 
                              style={{ 
                                padding: '1rem', 
                                fontSize: '0.875rem',
                                verticalAlign: 'top'
                              }}
                              rowSpan={orderItems.length}
                            >
                              {order.customer_name}
                            </td>
                            <td 
                              style={{ 
                                padding: '1rem', 
                                fontSize: '0.875rem',
                                verticalAlign: 'top'
                              }}
                              rowSpan={orderItems.length}
                            >
                              {order.customer_phone}
                            </td>
                            <td 
                              style={{ 
                                padding: '1rem', 
                                fontSize: '0.875rem',
                                verticalAlign: 'top',
                                maxWidth: '200px',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}
                              rowSpan={orderItems.length}
                            >
                              {order.address || 'не указан'}
                            </td>
                            <td 
                              style={{ 
                                padding: '1rem', 
                                fontSize: '0.875rem',
                                verticalAlign: 'top',
                                maxWidth: '200px',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}
                              rowSpan={orderItems.length}
                            >
                              {order.comment || '—'}
                            </td>
                          </>
                        )}
                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                          {item.product_name || 'Товар'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', textAlign: 'center' }}>
                          {item.quantity}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', textAlign: 'right' }}>
                          {(item.quantity * item.price).toFixed(2)} ₽
                        </td>
                        {/* Показываем общую сумму только в первой строке */}
                        {itemIndex === 0 && (
                          <td 
                            style={{ 
                              padding: '1rem', 
                              fontSize: '0.875rem', 
                              textAlign: 'right', 
                              fontWeight: '600',
                              verticalAlign: 'top'
                            }}
                            rowSpan={orderItems.length}
                          >
                            {order.total_amount} ₽
                          </td>
                        )}
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminPanel;

